/**
 * Accent-token derivation (addresses the design-stage carry-forward: derive
 * `--accent-strong` and `--on-accent` from luminance, not by aliasing).
 *
 * A tenant supplies ONE accent color. From it we compute:
 *  - `--accent-strong`: the accent darkened until a chosen text color clears
 *    WCAG AA (4.5:1) on it, so primary buttons never fail contrast — even for a
 *    pale tenant accent.
 *  - `--on-accent`: black or white, whichever contrasts better on the button.
 *  - `--accent-weak`: a scheme-aware tint for selected/hover surfaces.
 */

interface Rgb {
  r: number;
  g: number;
  b: number;
}

const DARK_INK = "#0b1f17";
const WHITE = "#ffffff";

export function hexToRgb(hex: string): Rgb {
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return { r: 26, g: 127, b: 90 }; // fallback: brand green
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function toHex({ r, g, b }: Rgb): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  const h = (n: number) => clamp(n).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

/** WCAG relative luminance (0 = black, 1 = white). */
export function luminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const chan = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * chan(r) + 0.7152 * chan(g) + 0.0722 * chan(b);
}

/** WCAG contrast ratio between two colors (1–21). */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Mix two hex colors; weight is the share of `a` (0..1). */
export function mix(a: string, b: string, weight: number): string {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  const w = Math.max(0, Math.min(1, weight));
  return toHex({
    r: ca.r * w + cb.r * (1 - w),
    g: ca.g * w + cb.g * (1 - w),
    b: ca.b * w + cb.b * (1 - w),
  });
}

/** Darken toward black by `amount` (0..1). */
export function darken(hex: string, amount: number): string {
  return mix("#000000", hex, amount);
}

export interface AccentTokens {
  accent: string;
  accentStrong: string;
  onAccent: string;
  accentWeak: string;
}

/**
 * Derive the accent token set for the given color scheme. `accentStrong` is
 * darkened iteratively until `onAccent` clears AA on it — guaranteeing legible
 * primary buttons regardless of the tenant's accent lightness.
 */
export function deriveAccentTokens(accent: string, dark: boolean): AccentTokens {
  let strong = accent;
  const bestOn = (bg: string) =>
    contrastRatio(WHITE, bg) >= contrastRatio(DARK_INK, bg) ? WHITE : DARK_INK;
  let onAccent = bestOn(strong);

  for (let i = 0; i < 24 && contrastRatio(onAccent, strong) < 4.5; i += 1) {
    strong = darken(strong, 0.06);
    onAccent = bestOn(strong);
  }

  const accentWeak = dark ? mix(accent, "#0c1512", 0.22) : mix(accent, "#ffffff", 0.14);
  return { accent, accentStrong: strong, onAccent, accentWeak };
}
